import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, HandCoins, Check, X, Copy } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader, Table } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Payouts() {
  const queryClient = useQueryClient();
  const [rate, setRate] = useState<string>("");
  const [paying, setPaying] = useState<any>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["admin-payouts"],
    queryFn: async () => (await api.get("/admin/payouts")).data,
  });
  const { data: requests } = useQuery({
    queryKey: ["admin-payout-requests"],
    queryFn: async () => (await api.get("/admin/payout-requests?status=pending")).data,
  });

  const resolveRequest = async (req: any, action: "paid" | "reject") => {
    try {
      const res = await api.patch(`/admin/payout-requests/${req.id}`, { action });
      toast.success(res.data.message);
      queryClient.invalidateQueries({ queryKey: ["admin-payout-requests"] });
      queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (data && rate === "") setRate(String(data.rate_per_1k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saveRate = async () => {
    try {
      await api.put("/admin/payouts/config", { rate_per_1k: rate });
      toast.success(`Rate updated — $${rate} per 1,000 views`);
      queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPay = (a: any) => {
    setPaying(a);
    setAmount(a.balance > 0 ? String(a.balance) : "");
    setNote("");
  };

  const recordPayout = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/payouts", { author_id: paying.author_id, amount, note });
      toast.success(res.data.message);
      setPaying(null);
      queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-payouts-page">
      <PageHeader title="Author Payouts" sub="Authors earn a flat rate per 1,000 views on their published stories — record payouts as you send them"
        actions={
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">$ / 1k views</span>
            <Input type="number" min="0" step="0.05" value={rate} onChange={(e) => setRate(e.target.value)}
              data-testid="payout-rate-input" className="w-24 bg-[#1A1A1A] border-white/10 text-white font-mono h-9" />
            <Button onClick={saveRate} data-testid="payout-rate-save"
              className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] h-9">
              <Save className="h-4 w-4" />
            </Button>
          </div>
        } />

      {(requests || []).length > 0 && (
        <div className="mb-8" data-testid="payout-requests-queue">
          <h3 className="font-heading font-bold text-white text-base mb-3">
            Withdrawal requests <span className="font-mono text-xs text-yellow-400">({requests.length} pending)</span>
          </h3>
          <div className="space-y-2">
            {requests.map((r: any) => (
              <div key={r.id} data-testid={`payout-request-row-${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 bg-[#14120a] border border-yellow-500/25 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <p className="text-white font-medium">
                    {r.name || r.email}
                    <span className="font-mono text-lg font-bold text-[#22c55e] ml-3">${r.amount.toFixed(2)}</span>
                  </p>
                  <button type="button" data-testid={`copy-paypal-${r.id}`}
                    onClick={async () => { await navigator.clipboard.writeText(r.paypal_email); toast.success("PayPal email copied"); }}
                    className="font-mono text-xs text-[#00E5FF] hover:underline inline-flex items-center gap-1 mt-0.5">
                    {r.paypal_email} <Copy className="h-3 w-3" />
                  </button>
                  <span className="font-mono text-[10px] text-gray-500 ml-3">requested {timeAgo(r.created_at)}</span>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => resolveRequest(r, "paid")} data-testid={`request-paid-${r.id}`}
                    className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                    <Check className="h-3.5 w-3.5 mr-1" /> Mark paid
                  </Button>
                  <Button size="sm" onClick={() => resolveRequest(r, "reject")} data-testid={`request-reject-${r.id}`}
                    className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8">
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Table headers={["Author", "Stories", "Views", "Accrued", "Paid", "Balance", "Actions"]}>
        {(data?.authors || []).map((a: any) => (
          <tr key={a.author_id} data-testid={`payout-row-${a.author_id}`}>
            <td className="px-4 py-3">
              <p className="text-white font-medium">{a.name}</p>
              <p className="font-mono text-[10px] text-gray-500">{a.email}</p>
            </td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{a.articles}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{a.views.toLocaleString()}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">${a.accrued.toFixed(2)}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">${a.paid.toFixed(2)}</td>
            <td className={`px-4 py-3 font-mono text-sm font-bold ${a.balance > 0 ? "text-[#22c55e]" : "text-gray-500"}`}>
              ${a.balance.toFixed(2)}
            </td>
            <td className="px-4 py-3">
              <Button size="sm" onClick={() => openPay(a)} data-testid={`payout-record-${a.author_id}`}
                className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                <HandCoins className="h-3.5 w-3.5 mr-1" /> Record payout
              </Button>
            </td>
          </tr>
        ))}
      </Table>
      {(data?.authors || []).length === 0 && (
        <p className="text-gray-500 text-sm mt-6 text-center py-8 border border-dashed border-white/10 rounded-lg">
          No approved authors yet — payouts appear once authors join and publish.
        </p>
      )}

      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Record payout — {paying?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-mono text-xs text-gray-400">
              Outstanding balance: <span className="text-[#22c55e] font-bold">${paying?.balance?.toFixed(2)}</span>
            </p>
            <div>
              <Label>Amount (USD)</Label>
              <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="payout-amount-input" className="mt-1 bg-[#1A1A1A] border-white/10 text-white font-mono" />
            </div>
            <div>
              <Label>Note (optional — e.g. "PayPal June")</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="payout-note-input" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            <Button onClick={recordPayout} disabled={busy || !amount} data-testid="payout-confirm-button"
              className="bg-[#22c55e] text-black font-bold hover:bg-[#1ea34d] w-full">
              {busy ? "Recording…" : "Record payout"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
